import * as THREE from 'three';
import RAPIER from '@dimforge/rapier3d-compat';
import { type Tool } from '../input/actions';
import { PhysicsCity, type Hit } from '../physics/world';
import { Cameras } from '../input/cameras';
import { Effects } from '../render/effects';
import { AudioSystem } from './audio';
import { BASE_C4_RADIUS, GROUND, LIMITS } from '../core/config';
import { physicalCenter } from '../destruction/structure';
import type { EntityAttachment, EntityRegistry } from '../core/entities';
import { STRIKE_LENGTH, STRIKE_RADIUS } from './airstrikes';
export interface Charge {
  id: number;
  mesh: THREE.Mesh;
  cellId?: number;
  local: THREE.Vector3;
  localQ: THREE.Quaternion;
  targetBody?: RAPIER.RigidBody;
  looseBody?: RAPIER.RigidBody;
  attachment?: EntityAttachment;
  fixed?: boolean;
  epoch: number;
}
export class Tools {
  selected: Tool = 'c4';
  charges: Charge[] = [];
  holding = false;
  hold = 0;
  holdFired = false;
  cooldown = 0;
  target: Hit | null = null;
  marker: THREE.Mesh;
  preview: THREE.Mesh;
  approach = new THREE.ArrowHelper(new THREE.Vector3(-1, 0, 0), new THREE.Vector3(), 80, 0xf1d599, 8, 4);
  onPlaneCrash: (point: THREE.Vector3, direction: THREE.Vector3) => boolean = () => false;
  onAirstrike: (point: THREE.Vector3, direction: THREE.Vector3) => boolean = () => false;
  private strip = new THREE.LineLoop(
    new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(-STRIKE_RADIUS, 0, -STRIKE_LENGTH / 2),
      new THREE.Vector3(STRIKE_RADIUS, 0, -STRIKE_LENGTH / 2),
      new THREE.Vector3(STRIKE_RADIUS, 0, STRIKE_LENGTH / 2),
      new THREE.Vector3(-STRIKE_RADIUS, 0, STRIKE_LENGTH / 2),
    ]),
    new THREE.LineBasicMaterial({ color: 0xf1d599, depthTest: false, transparent: true, opacity: 0.7 }),
  );
  private heldDirection = new THREE.Vector3();
  c4Footprint = 10;
  c4Strength = 1;
  planeRadius = 20.3;
  onMessage: (text: string) => void = () => {};
  canAttach: (body: RAPIER.RigidBody) => boolean = () => false;
  private sequence = 0;
  private heldPoint?: THREE.Vector3;
  private chargeGeometry = new THREE.BoxGeometry(0.38, 0.18, 0.5);
  private chargeMaterial = new THREE.MeshStandardMaterial({
    color: 0xcd704a,
    emissive: 0x421309,
    roughness: 0.65,
  });
  constructor(
    readonly physics: PhysicsCity,
    readonly cameras: Cameras,
    readonly effects: Effects,
    readonly audio: AudioSystem,
    readonly entities?: EntityRegistry,
  ) {
    this.marker = new THREE.Mesh(
      new THREE.RingGeometry(0.26, 0.34, 32),
      new THREE.MeshBasicMaterial({
        color: 0xffdd9a,
        side: THREE.DoubleSide,
        depthTest: false,
        transparent: true,
        opacity: 0.9,
      }),
    );
    this.marker.renderOrder = 10;
    physics.scene.add(this.marker);
    this.preview = new THREE.Mesh(
      new THREE.RingGeometry(0.985, 1, 64),
      new THREE.MeshBasicMaterial({
        color: 0xffd990,
        transparent: true,
        opacity: 0.4,
        side: THREE.DoubleSide,
        depthTest: false,
        depthWrite: false,
      }),
    );
    this.preview.rotation.x = -Math.PI / 2;
    this.preview.renderOrder = 9;
    this.preview.visible = false;
    physics.scene.add(this.preview);
    this.approach.visible = false;
    physics.scene.add(this.approach);
    this.strip.visible = false;
    physics.scene.add(this.strip);
  }
  select(tool: Tool): void {
    // Old external/session selections fall back to the supported default.
    this.selected = tool === 'plane' || tool === 'airstrike' ? tool : 'c4';
    this.cooldown = 0;
    this.cancelHold();
  }
  cancelHold(): void {
    this.holding = false;
    this.hold = 0;
    this.holdFired = false;
    this.heldPoint = undefined;
  }
  start(): void {
    if (this.physics.life.phase === 'playing' && this.selected !== 'c4') {
      const h = this.cameras.aim();
      if (!h) return;
      this.holding = true;
      this.hold = 0;
      this.holdFired = false;
      this.heldPoint = h.point.clone();
      this.heldDirection.copy(this.cameras.camera.getWorldDirection(new THREE.Vector3())).setY(0).normalize();
    }
  }
  use(): void {
    if (this.physics.life.phase !== 'playing' || this.cooldown > 0 || this.selected !== 'c4') return;
    const h = this.cameras.aim();
    if (!h) return;
    this.cooldown = 0.16;
    this.placeCharge(h);
  }
  placeCharge(hit: Hit): void {
    if (hit.dormant) {
      hit.cluster = this.physics.activate(hit.dormant.id);
      if (!hit.cluster) {
        this.onMessage('That structure changed before placement. Try its current surface.');
        return;
      }
    }
    const body = hit.collider?.parent() ?? hit.cluster?.body ?? undefined;
    if (this.charges.length >= LIMITS.charges) {
      this.onMessage('Detonate the placed charges before adding more.');
      return;
    }
    const mesh = new THREE.Mesh(this.chargeGeometry, this.chargeMaterial);
    mesh.position.copy(hit.point).addScaledVector(hit.normal, 0.1);
    mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), hit.normal);
    const rotation = body?.rotation() ?? { x: 0, y: 0, z: 0, w: 1 },
      q = new THREE.Quaternion(rotation.x, rotation.y, rotation.z, rotation.w);
    let local: THREE.Vector3, cellId: number | undefined;
    if (hit.cluster) {
      const c = hit.cluster,
        inside = this.physics.toLocal(c, hit.point.clone().addScaledVector(hit.normal, -0.04));
      let dist = Infinity;
      for (const cell of c.cells.values()) {
        const p = physicalCenter(cell),
          d = inside.distanceToSquared(new THREE.Vector3(p.x, p.y, p.z));
        if (d < dist) {
          dist = d;
          cellId = cell.id;
        }
      }
      local = this.physics.toLocal(c, mesh.position);
      if (cellId !== undefined) this.physics.pinnedCells.add(cellId);
    } else if (body && !body.isFixed()) {
      const p = body.translation();
      local = mesh.position
        .clone()
        .sub(new THREE.Vector3(p.x, p.y, p.z))
        .applyQuaternion(q.clone().invert());
    } else local = mesh.position.clone();
    const attachment = hit.entity
      ? this.entities?.attach(hit.entity, mesh.position, mesh.quaternion, hit.anchor)
      : undefined;
    if (hit.entity && !attachment) {
      this.onMessage('That target has changed. Place again on its current surface.');
      return;
    }
    this.physics.scene.add(mesh);
    this.charges.push({
      id: ++this.sequence,
      mesh,
      cellId,
      local,
      localQ: q.clone().invert().multiply(mesh.quaternion),
      targetBody: hit.cluster ? undefined : body,
      attachment,
      fixed: !hit.cluster && !attachment && (!body || body.isFixed()),
      epoch: this.physics.life.epoch,
    });
    this.audio.play('wood', 0.3);
    this.onMessage(`${this.charges.length}/${LIMITS.charges} placed · X to detonate`);
  }
  detonate(): void {
    if (this.physics.life.phase !== 'playing' || !this.charges.length) return;
    this.updateCharges();
    const charges = this.charges;
    this.charges = [];
    const groups = new Map<string, { point: THREE.Vector3; count: number }>();
    for (const charge of charges) {
      const point = charge.mesh.position.clone();
      this.release(charge);
      // Exactly coincident charges add HP and heat, without changing radius or
      // falsely treating accumulated damage as pulverising fracture energy.
      const key = `${point.x},${point.y},${point.z}`;
      const prior = groups.get(key);
      if (prior) prior.count++;
      else groups.set(key, { point, count: 1 });
    }
    for (const { point, count } of groups.values()) {
      this.physics.blast(point, this.c4Radius, 7.3 * this.c4Strength, true, 'c4', 0.85, count);
      this.effects.burst(point, 0xc8baa7, Math.min(60, 28 + Math.log2(count) * 4), 7);
    }
    this.audio.play('blast', 1.6);
    this.onMessage('Charges detonated');
  }
  private updateCharges(): void {
    for (const charge of this.charges) {
      if (!this.physics.life.valid(charge.epoch)) continue;
      if (
        charge.attachment &&
        this.entities?.follow(charge.attachment, charge.mesh.position, charge.mesh.quaternion)
      )
        continue;
      if (charge.fixed) continue;
      const owner =
        charge.cellId === undefined
          ? undefined
          : this.physics.clusters.get(this.physics.cellOwner.get(charge.cellId) ?? -1);
      if (owner) {
        charge.mesh.position.copy(charge.local).sub(owner.origin).applyMatrix4(owner.mesh.matrix);
        const q = owner.mesh.quaternion;
        charge.mesh.quaternion.set(q.x, q.y, q.z, q.w).multiply(charge.localQ);
      } else if (!charge.attachment && charge.targetBody?.isValid()) {
        const p = charge.targetBody.translation(),
          r = charge.targetBody.rotation(),
          q = new THREE.Quaternion(r.x, r.y, r.z, r.w);
        charge.mesh.position
          .copy(charge.local)
          .applyQuaternion(q)
          .add(new THREE.Vector3(p.x, p.y, p.z));
        charge.mesh.quaternion.copy(q).multiply(charge.localQ);
      } else if (!charge.looseBody) {
        const p = charge.mesh.position;
        charge.looseBody = this.physics.world.createRigidBody(
          RAPIER.RigidBodyDesc.dynamic().setTranslation(p.x, p.y, p.z).setCcdEnabled(true),
        );
        this.physics.world.createCollider(
          RAPIER.ColliderDesc.cuboid(0.19, 0.09, 0.25).setDensity(3),
          charge.looseBody,
        );
      } else {
        const p = charge.looseBody.translation(),
          q = charge.looseBody.rotation();
        charge.mesh.position.set(p.x, p.y, p.z);
        charge.mesh.quaternion.set(q.x, q.y, q.z, q.w);
      }
    }
  }
  get c4Radius(): number {
    return BASE_C4_RADIUS * Math.sqrt(this.c4Footprint);
  }
  step(dt: number): void {
    this.cooldown = Math.max(0, this.cooldown - dt);
  }
  update(dt: number, _alpha: number, allow = true): void {
    this.updateCharges();
    this.target = this.cameras.aim();
    const h = this.target;
    this.marker.visible = allow && !!h;
    this.preview.visible = allow && !!h && this.selected !== 'airstrike';
    this.strip.visible = allow && !!h && this.selected === 'airstrike';
    this.approach.visible = allow && !!h && this.selected !== 'c4';
    if (h && this.approach.visible) {
      const direction = this.holding
        ? this.heldDirection
        : this.cameras.camera.getWorldDirection(new THREE.Vector3()).setY(0).normalize();
      this.approach.setDirection(direction);
      this.strip.position.copy(this.heldPoint ?? h.point).add(new THREE.Vector3(0, 0.5, 0));
      this.strip.rotation.y = Math.atan2(direction.x, direction.z);
      this.approach.position
        .copy(h.point)
        .addScaledVector(direction, -80)
        .add(new THREE.Vector3(0, 0.8, 0));
    }
    if (h) {
      const point = h.point;
      this.preview.position.set(point.x, Math.max(GROUND + 0.04, point.y + 0.04), point.z);
      this.preview.scale.setScalar(this.selected === 'plane' ? this.planeRadius : this.c4Radius);
    }
    if (h) {
      this.marker.position.copy(h.point).addScaledVector(h.normal, 0.045);
      this.marker.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), h.normal);
      this.marker.scale.setScalar(Math.max(1, this.cameras.camera.position.distanceTo(h.point) * 0.01));
      (this.marker.material as THREE.MeshBasicMaterial).color.setHex(0xffdc94);
    }
    if (this.holding && allow && !this.holdFired) {
      this.hold += dt;
      if (this.hold >= 1) {
        this.holdFired = true;
        if (this.heldPoint && this.selected === 'airstrike')
          this.onMessage(
            this.onAirstrike(this.heldPoint, this.heldDirection)
              ? 'Bombing run inbound · eight bombs across the marked strip'
              : 'Airstrike unavailable while the city is rebuilding',
          );
        if (this.heldPoint && this.selected === 'plane')
          this.onMessage(
            this.onPlaneCrash(this.heldPoint, this.heldDirection)
              ? 'Unoccupied cargo transport on approach'
              : 'Two test aircraft already approaching · wait for impact',
          );
      }
    }
  }
  private release(charge: Charge): void {
    this.physics.scene.remove(charge.mesh);
    if (charge.looseBody?.isValid()) this.physics.world.removeRigidBody(charge.looseBody);
    if (charge.cellId !== undefined) this.physics.pinnedCells.delete(charge.cellId);
  }
  clear(): void {
    for (const c of this.charges) this.release(c);
    this.charges = [];
    this.cancelHold();
    this.cooldown = 0;
    this.preview.visible = false;
    this.approach.visible = false;
    this.strip.visible = false;
    this.effects.clear();
  }
}
